import { arcTitleOf, cardHeight } from '../plane/cardSize';
import type { CardKind } from '../plane/cardSize';
import { ulidTime } from '../entity/catalog';
import { refKey } from '../plane/planeKey';
import type { PlanePlacement, PlaneRef } from '../plane/planeKey';
import type { Workspace } from '../workspaces/workspace';
import { draftNooks } from '../workspaces/workspace';
import { pinnedNooks } from '../workspaces/pinned';
import type { DeskContainer, DeskEntry } from './deskContainer';
import { entryFor, filedEntry, nookPathsOf } from './deskContainer';

/** What stands on the desk or the shelf: a nook, or a recording, note or topic that is listed. */
export interface DeskItem {
  key: string;
  ref: PlaneRef;
  /** The entry as the container wrote it, where it is listed. */
  entry: DeskEntry;
  /** Set for a nook. */
  nook: Workspace | null;
  pinned: boolean;
  /** A live draft: nobody has kept it yet. */
  draft: boolean;
}

export interface DeskMembers {
  desk: DeskItem[];
  shelf: DeskItem[];
  /** The live drafts nobody has pinned, newest made first: they wait beside the table. */
  drafts: DeskItem[];
}

/**
 * Who is on the desk, who is on the shelf and which drafts wait beside the table.
 *
 * A nook stands on the desk only when it is pinned. A nook `desk:` lists that is not pinned reads
 * as shelf when `shelf:` lists it, and is otherwise just a nook in the list; its `desk:` line is
 * left as it is. A recording, note, topic or sticky stands on the desk when listed. A draft nobody
 * has pinned is a draft whatever `desk:` or `plane:` say. An `id:` entry is the nook whose `id` it
 * names, once that nook has a file. Archived nooks and ones held inside another are on none.
 * The desk runs as the app's does: first the pinned nooks `desk:` does not list, in
 * [newestMadeFirst] order, where the next container write lists them; then `desk:` as it has it
 * (newest first). With no container there is no list: the desk is the pinned nooks, and there is
 * no shelf.
 */
export function deskMembers(container: DeskContainer | null, nooks: readonly Workspace[]): DeskMembers {
  const byPath = new Map(nooks.map((n) => [n.path, n]));
  const paths = nookPathsOf(nooks);
  const live = (n: Workspace) => !n.archived && !n.inside;

  const itemOf = (listed: DeskEntry): DeskItem | null => {
    const entry = filedEntry(listed, paths);
    if (entry.unfiled) return null;
    const nook = byPath.get(entry.ref.ref) ?? null;
    // A path under spaces/ that no nook reads is no card; anything else is a recording or note.
    if (!nook && entry.ref.ref.startsWith('spaces/')) return null;
    if (nook && !live(nook)) return null;
    return {
      key: refKey(entry.ref),
      ref: entry.ref,
      entry,
      nook,
      pinned: !!nook && nook.pinned !== null,
      draft: !!nook && nook.draft,
    };
  };

  const seen = new Set<string>();
  const once = (item: DeskItem | null): item is DeskItem => {
    if (!item || seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  };
  const standing = (item: DeskItem | null): item is DeskItem => !!item && (!item.nook || item.pinned);
  const listed = (container?.desk ?? []).map(itemOf).filter(standing).filter(once);
  const unlisted = pinnedNooks(nooks)
    .sort(newestMadeFirst)
    .map((nook) => itemOf(entryFor({ ref: nook.path, anchors: [] })))
    .filter(once);
  const shelf = (container?.shelf ?? []).map(itemOf).filter(once);
  const drafts = draftNooks(nooks)
    .sort(newestMadeFirst)
    .map((nook) => itemOf(entryFor({ ref: nook.path, anchors: [] })))
    .filter(once);
  return { desk: [...unlisted, ...listed], shelf, drafts };
}

const DAY = 24 * 60 * 60 * 1000;
const RECENT_DAYS = 2;
const RECENT_LIMIT_DAYS = 14;
const RECENT_FLOOR = 6;

/**
 * The drafts the tray shows, from [drafts] newest made first: every one from the last 2 days, and
 * where fewer than 6 are that recent, the newest of those from the last 14 days up to 6. A draft
 * whose file does not say when it was made counts as older. The rest are [older], not archived.
 */
export function recentDrafts(drafts: readonly DeskItem[], nowMs: number): { shown: DeskItem[]; older: DeskItem[] } {
  const age = (item: DeskItem) => {
    const made = item.nook ? madeMs(item.nook) : null;
    return made === null ? Infinity : nowMs - made;
  };
  const fresh = drafts.filter((d) => age(d) <= RECENT_DAYS * DAY).length;
  const inLimit = drafts.filter((d) => age(d) <= RECENT_LIMIT_DAYS * DAY).length;
  const count = Math.max(fresh, Math.min(RECENT_FLOOR, inLimit));
  return { shown: drafts.slice(0, count), older: drafts.slice(count) };
}

const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

/** When a nook was made, as its file says: when its id was minted, else the day it was started. */
function madeMs(nook: Workspace): number | null {
  if (nook.id && ULID.test(nook.id)) return ulidTime(nook.id);
  const day = nook.started ? Date.parse(nook.started) : NaN;
  return Number.isNaN(day) ? null : day;
}

/**
 * The order the app lists desk cards no list holds yet in: the newest arrival on the desk first,
 * then by id. When a card arrived is kept beside the nook in the app alone, so this goes by when the
 * nook was made, which is what the app falls back to for a card with no arrival.
 */
export function newestMadeFirst(a: Workspace, b: Workspace): number {
  const made = (madeMs(b) ?? -Infinity) - (madeMs(a) ?? -Infinity);
  if (made !== 0 && !Number.isNaN(made)) return made;
  const ai = a.id ?? a.path;
  const bi = b.id ?? b.path;
  return ai < bi ? -1 : ai > bi ? 1 : 0;
}

export interface SpotBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The room kept clear around a card, and the step a free spot is looked for in. */
export const GAP = 12;

/**
 * Where cards with no plane line are drawn: for each of [wanted], in order, the first free spot
 * scanning left to right and then top to bottom in steps of [GAP], from the top-left of what is
 * [placed] (negative positions included) across [innerWidth], clear of every placed box and of the
 * spots already given by [GAP]. With none free it is the first row under everything. Held in
 * memory; nothing here writes.
 */
export function freeSpots(
  placed: readonly SpotBox[],
  wanted: readonly { key: string; w: number; h: number }[],
  innerWidth: number,
): Map<string, { x: number; y: number }> {
  const taken: SpotBox[] = [...placed];
  const out = new Map<string, { x: number; y: number }>();
  const left = placed.length > 0 ? Math.min(...placed.map((p) => p.x)) : 0;
  const top = placed.length > 0 ? Math.min(...placed.map((p) => p.y)) : 0;
  const clear = (box: SpotBox) =>
    taken.every(
      (t) => box.x + box.w + GAP <= t.x || t.x + t.w + GAP <= box.x || box.y + box.h + GAP <= t.y || t.y + t.h + GAP <= box.y,
    );
  for (const card of wanted) {
    const bottom = taken.length > 0 ? Math.max(...taken.map((t) => t.y + t.h)) : top;
    // The first row of the grid that is clear of everything: where the scan ends if nothing is free before.
    const under = top + Math.ceil((bottom + GAP - top) / GAP) * GAP;
    let found: { x: number; y: number } | null = null;
    for (let y = top; y <= under && found === null; y += GAP) {
      for (let x = left; x + card.w <= left + innerWidth && found === null; x += GAP) {
        if (clear({ x, y, w: card.w, h: card.h })) found = { x, y };
      }
    }
    found ??= { x: left, y: under };
    taken.push({ ...found, w: card.w, h: card.h });
    out.set(card.key, found);
  }
  return out;
}

/** A placed card's box on the desk before it is measured: its width; a note's own size, else its
 *  `~N` or a default; any other card's `~N`, else its kind's estimate. */
export function boxOf(
  placement: Pick<PlanePlacement, 'ref' | 'x' | 'y' | 'lines' | 'w' | 'h'>,
  kind: CardKind,
  width: number,
): SpotBox {
  return {
    x: placement.x,
    y: placement.y,
    w: placement.w ?? width,
    h:
      kind === 'note'
        ? (placement.h ?? cardHeight(placement, 'other'))
        : cardHeight(placement, kind, kind === 'arc' ? arcTitleOf(placement.ref) : null),
  };
}
